// i18n ignore
import ChatSdk from '@tencent/wuji-chat-sdk';
import { XyFlowTypeSelectorMeta, XyGPTAlertMeta, AiModelSelectMeta } from '../paramsFormComponents';
import { CATEGORY } from './const';
import { isPageletComponentRenderer } from '@components/pagelet-component/utils';
import { getTraceIdHeaders, AI_MODULE_MAP } from '@/modules/ai/helper';

const chatSdk = new ChatSdk({
  dns: `${location.host}${window.GLOBAL_INFO?.pathPrefix || ''}`,
  proto: location.protocol.slice(0, -1),
  isRuntime: true,
});

export const AI_MODELS_OPTIONS = [
  {
    value: 'kimi-k2-instruct-local',
    label: 'Kimi-K2-Instruct',
  },
  {
    value: 'hunyuan-turbos-vision-latest',
    label: 'Hunyuan Turbos',
  },
  {
    value: 'hunyuan-vision',
    label: 'Hunyuan Turbos Vision',
  },
  {
    value: 'gpt-4o',
    label: 'GPT-4o',
  },
  {
    value: 'gpt-5.1',
    label: 'GPT 5.1',
  },
  {
    value: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
  },
  {
    value: 'deepseek-v3',
    label: 'DeepSeek-V3',
  },
  {
    value: 'deepseek-r1',
    label: 'DeepSeek-R1',
  },
];

// 消息返回类型
const RETURN_TYPE = {
  sync: 'sync',
  flow: 'flow',
};

// 流式响应类型，拼接字符串、调用LessCode方法
const FLOW_TYPE = {
  joinString: 'joinString',
  callMethod: 'callMethod',
};

// 生成sessionId
const getSessionId = () => `${Math.random().toString(16)
  .slice(2, 10)}`;

// 根据路径拼接字符串
const appendValueByPath = (obj, path, value) => {
  try {
    const props = path.split('.');
    let current = obj;
    for (let i = 0; i < props.length - 1; i++) {
      current = current[props[i]];
    }
    current[props[props.length - 1]] += value;
  } catch (error) {
    console.error('路径错误！', error);
  }
};

const getErrorMessage = (message = '') => `[行为]AI模型调用出错：${message}`;

// 发送消息
const sendData = (params, renderer) => new Promise(async (resolve) => {
  const {
    message,
    systemContent,
    type,
    flowSetting: { flowType, joinString, callMethod },
    config,
    model,
    hunyuanModel,
  } = params;
  let { system } = params;
  let queryStr = window.w.projectId ? `projectId=${window.w.projectId}` : '';
  if (window.w.mainPageInfo?.pageId) {
    queryStr += `&pageId=${window.w.mainPageInfo.pageId}`;
  }
  const sessionId = getSessionId();
  let event;
  const modelConfig = config || {};
  if (system === 'hy') {
    system = 'azure';
    modelConfig.model = hunyuanModel || model || 'hunyuan';
  } else if (model) {
    modelConfig.model = model || AI_MODELS_OPTIONS[0].value;
  }

  if (Array.isArray(message)) {
    if (systemContent && message?.[0]?.role !== 'system') {
      message.unshift({
        role: 'system',
        content: systemContent,
      });
    }
    event = await chatSdk.sendAll({
      messages: message,
      sessionId,
      modelConfig,
    }, {
      system,
      queryStr,
    });
  } else {
    const params = {
      sessionId,
    };
    if (message) {
      params.message = message;
      systemContent && (params.systemContent = systemContent);
    } else if (systemContent) {
      // 兼容只有prompt，没有message的情况
      params.message = systemContent;
    }
    params.modelConfig = modelConfig;
    event = await chatSdk.send(
      params,
      {
        system,
        queryStr,
        headers: {
          ...getTraceIdHeaders(AI_MODULE_MAP.RUNTIME_AI_ATOM),
        },
      },
    );
  }

  let content = '';
  if (type === RETURN_TYPE.flow) {
    event.on('data', (data) => {
      const responseMessage = data.delta === undefined ? '' : data.delta;
      if (flowType === FLOW_TYPE.joinString) {
        appendValueByPath(renderer, joinString, responseMessage);
        content += responseMessage;
      }
      if (flowType === FLOW_TYPE.callMethod) {
        let methodName = callMethod;
        let context = null;
        if (methodName?.startsWith('$app.')) {
          methodName = methodName.replace('$app.', '');
          context = Vue.prototype.$app;
        } else {
          let topRenderer = renderer;
          while (topRenderer.ucParentRenderer && !isPageletComponentRenderer(topRenderer.$el)) {
            topRenderer = topRenderer.ucParentRenderer;
          }
          context = topRenderer;
        }

        let topRenderer = renderer;
        while (topRenderer.ucParentRenderer && !isPageletComponentRenderer(topRenderer.$el)) {
          topRenderer = topRenderer.ucParentRenderer;
        }
        (context[methodName](responseMessage));
      }
    });
  }

  event.on('end', (data) => {
    resolve({
      content: data.value,
      sessionId,
    });
  });

  event.on('error', (error) => {
    resolve({
      content,
      sessionId,
      errorMessage: error.error,
    });
  });
});

export default [
  {
    id: 'xy:ai:sendMessage',
    name: 'AI模型调用',
    desc: '',
    category: CATEGORY.AI,
    paramsSchema: {
      type: 'object',
      fields: [
        {
          id: 'alert',
          title: '',
          type: 'string',
          default: '',
          ui: { type: 'XyGPTAlert' },
          'uc:allowExpr': false,
          'uc:allowReset': false,
        },
        {
          id: 'systemContent',
          title: '预置prompt',
          type: 'string',
          default: '',
          'uc:allowInterop': true,
          description: '当message为数组时，prompt会作为第一条信息插入message数组，但如果message数组第一条就是"system"，则忽略该prompt',
        },
        {
          id: 'message',
          title: 'message（文本或者数组）',
          type: 'string',
          default: '',
          description: `message支持文本、对话上下文数组。数组格式如下：
[{
  "role": "system",
  "content": "你是一个只会用中文回答问题的助理"
},
{
  "role": "user",
  "content": "Hi！"
},
{
  "role": "assistant",
  "content": "你好，有什么我可以帮助你的吗？"
}]`,
          'uc:allowInterop': true,
        },
        {
          id: 'showErrorNotification',
          title: '错误提示',
          type: 'boolean',
          description: '勾选后,AI请求出错会弹窗并阻塞后续流程运行',
          default: true,
        },
        {
          id: 'config',
          title: '模型参数',
          type: 'object',
          'uc:allowInterop': true,
          fields: [{
            id: 'temperature',
            title: '温度',
            type: 'number',
            default: 1,
            description: '整型, 从0-2取值,默认是1.值越大输出越不稳定.',
          }],
          description: '可用于传递给AI模型的额外参数, 例如 temperature, top_p, response_format 等. \n如需拓展请使用右上角小齿轮.',
        },
        {
          id: 'type',
          title: '返回消息形式',
          type: 'string',
          enum: [
            {
              value: RETURN_TYPE.sync,
              label: '同步返回（等待全部消息接收完成后返回）',
            },
            {
              value: RETURN_TYPE.flow,
              label: '流式返回（拼接字符串或调用LessCode方法）',
            },
          ],
          default: RETURN_TYPE.sync,
        },
        {
          id: 'flowSetting',
          type: 'string',
          ui: { type: 'XyFlowTypeSelector' },
          title: '',
          condition: `params.type === "${RETURN_TYPE.flow}"`,
          default: {
            flowType: FLOW_TYPE.joinString,
            joinString: '',
            callMethod: 'onData',
          },
        },
        {
          id: 'model',
          title: '模型',
          type: 'string',
          default: AI_MODELS_OPTIONS[0].value,
          ui: {
            type: 'XyAiModelSelect',
          },
        },
      ],
    },
    execute: async (ctx, params) => {
      const { systemContent, message } = params;

      if (!systemContent && !message) {
        const msg = getErrorMessage('预置prompt、message不能同时为空！');
        throw new Error(msg);
      }

      const result = await sendData(params, ctx.renderer);
      if (result.errorMessage) {
        if (params.showErrorNotification) {
          throw new Error(getErrorMessage(result.errorMessage));
        }
      }
      return result;
    },
    returnValue: {
      type: 'object',
      fields: [
        {
          id: 'sessionId',
          description: '会话ID',
          type: 'string',
        },
        {
          id: 'content',
          description: '接收到的消息',
          type: 'string',
        },
        {
          id: 'errorMessage',
          description: '错误信息',
          type: 'string',
        },
      ],
    },
    paramsFormComponents: [
      XyFlowTypeSelectorMeta,
      XyGPTAlertMeta,
      AiModelSelectMeta,
    ],
  },
];
